import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import Modal from "../../components/Modal";
import PanelTemplate from "../../templates/Panel";
import Button from "../../components/Button";
import BarProgress from "../../components/BarProgress";
import Input from "../../components/Input";
import PlanFeatureUpgradeButton from "../../components/PlanFeatureUpgradeButton";
import PlanFeatureUpgradeModal from "../../components/PlanFeatureUpgradeModal";
import logoIntegracao from "../../assets/images/svgs/logo-integracao.svg";
import StatusBadge from "../../components/StatusBadge";
import Icon from "../../components/Icon";
import "./styles.scss";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { toast } from "react-toastify";
import { hasPlanFeature } from "../../services/auth";

const IntegrationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const hasCalculationFeature = hasPlanFeature("calculation");
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState("connect");
    const [step, setStep] = useState(1);
    const [supplierData, setSupplierData] = useState({});
    const [loading, setLoading] = useState(false);
    const [savingApiKeys, setSavingApiKeys] = useState(false);
    const [hasApiKeys, setHasApiKeys] = useState(true);
    const [integrationFieldsValues, setIntegrationFieldsValues] = useState({});
    const [editingKeys, setEditingKeys] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    useEffect(() => {
        getSupplier(id);
    }, [id]);


    const getSupplier = (id) => {
        setLoading(true);

        api.get(`/supplier/${id}`).then(res => {
            const data = res.data.resources;
            setSupplierData(data);
            const fields = data?.integration_fields || [];
            const initial = {};
            fields.forEach((f) => {
                initial[f.name] = "";
            });
            setIntegrationFieldsValues(initial);
            setHasApiKeys(hasCalculationFeature ? !!data?.has_api_keys : false);
            setEditingKeys(false);
        }).catch(error => {
            renderError("Falha ao carregar dados do fornecedor");
        }).then(() => setLoading(false));
    };

    const setIntegrationField = (name, value) => {
        setIntegrationFieldsValues((prev) => ({ ...prev, [name]: value }));
    };

    const buildAssociatePayload = () => {
        const base = { supplier_id: id };
        if (!supplierData.has_integration) {
            return base;
        }
        base.has_api_keys = hasApiKeys;
        if (hasApiKeys && (!supplierData.has_api_keys || editingKeys)) {
            Object.assign(base, integrationFieldsValues);
        }
        return base;
    };

    const handleConnectClick = () => {
        if (supplierData.has_integration && hasApiKeys && !validateIntegrationFields()) {
            return;
        }
        setModalType("connect");
        setShowModal(true);
    };

    const handleDisconnectClick = () => {
        setModalType("disconnect");
        setShowModal(true);
    };

    const getModalTitle = () => {
        return modalType === "connect" ? "Conectar fornecedor" : "Desconectar fornecedor";
    };

    const getModalButtonText = () => {
        return modalType === "connect" ? "Conectar fornecedor" : "Desconectar fornecedor";
    };

    const getButtonText = () => {
        return supplierData.is_associated === true ? "Desconectar fornecedor" : "Conectar fornecedor";
    };

    const getButtonClass = () => {
        return supplierData.is_associated === true ? "disconnect-btn" : "connect-btn";
    };

    const handleButtonClick = () => {
        if (supplierData.is_associated === true) {
            handleDisconnectClick();
        } else {
            handleConnectClick();
        }
    };

    const handleButtonModalClick = () => {
        toggleSupplier();
    };

    const validateIntegrationFields = () => {
        if (!supplierData.has_integration || !hasApiKeys) return true;
        if (supplierData.has_api_keys && !editingKeys) return true;
        const fields = supplierData.integration_fields || [];
        const required = fields.filter((f) => f.required);
        const missing = required.filter((f) => !(integrationFieldsValues[f.name] || "").trim());
        if (missing.length > 0) {
            toast.error(`Preencha o campo: ${missing[0].label}`);
            return false;
        }
        return true;
    };

    const toggleSupplier = () => {
        if (modalType === "connect" && !validateIntegrationFields()) return;
        setLoading(true);
        const payload = modalType === "disconnect"
            ? { supplier_id: id }
            : buildAssociatePayload();

        api.post(`/supplier/associate`, payload)
            .then(() => {
                window.location.reload();
            })
            .catch(error => {
                renderError(error);
                setLoading(false);
            });
    };

    const handleViewAllClick = () => {
        if (!supplierData.slug) {
            renderError("Slug do fornecedor não disponível");
            return;
        }

        // O catálogo é público — basta abrir a URL do fornecedor.
        const baseUrl = process.env.REACT_APP_CATALOG_URL;

        if (!baseUrl) {
            renderError("URL do catálogo não configurada");
            return;
        }

        window.open(`${baseUrl}/${supplierData.slug}`, '_blank');
    }

    const handleSaveCredentials = () => {
        if (!validateIntegrationFields()) return;

        setSavingApiKeys(true);

        const payload = {
            supplier_id: id,
            has_api_keys: hasApiKeys
        };

        console.log(hasApiKeys, editingKeys);
        if (hasApiKeys && (!supplierData.has_api_keys || editingKeys)) {
            Object.assign(payload, integrationFieldsValues);
        }

        api.put(`/supplier/api`, payload)
            .then(res => {
                toast.success(res.data.message || "Dados atualizados com sucesso.", {
                    autoClose: 10000
                });
                getSupplier(id);
            })
            .catch(error => {
                renderError(error);
            })
            .then(() => setSavingApiKeys(false));
    };

    return (
        <>
            <PlanFeatureUpgradeModal
                feature="calculation"
                show={showUpgradeModal}
                close={() => setShowUpgradeModal(false)}
            />
            <Modal
                show={showModal}
                close={() => setShowModal(false)}
                title={getModalTitle()}
                footer={
                    <>
                        <Button type="secondary" action={() => setShowModal(false)} text="Cancelar" />
                        <Button btnClass="btn-integration" 
                            action={handleButtonModalClick} 
                            text={getModalButtonText()} 
                            svg="icon-connect" 
                            loading={loading}
                        />
                    </>
                }
            >
                <div className="section section-integration">
                    <img src={supplierData.logo} alt={`Logo ${supplierData.trade_name}`} />
                    {step === 1 && (
                        <>
                            {modalType === "connect" ? (
                                <>
                                    <h3>Tem certeza que deseja conectar este fornecedor?</h3>
                                    <p>
                                        Serão cadastrados <div className="box-product">{supplierData.productCount} produtos</div> na sua conta.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3>Tem certeza que deseja desconectar este fornecedor?</h3>
                                    <p>
                                        Serão removidos <div className="box-product">{supplierData.productCount} produtos</div> da sua conta.
                                    </p>
                                </>
                            )}
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <h3>Conexão realizada com sucesso</h3>
                            <p>
                                O processo de importação de todos os produtos pode levar algum tempo, <br />
                                mas você pode acompanhar o status sempre que precisar.
                            </p>
                            <div className="box-margin">
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            <PanelTemplate id="integration-detail">
                <div className="integration-detail-page">
                    {!loading && (
                        <div className="detail-layout">
                            <div className="supplier-profile">
                                <div className="supplier-header">
                                    <div className="supplier-logo">
                                        <img src={supplierData.logo} alt={`Logo ${supplierData.trade_name}`} />
                                    </div>
                                    <div className="supplier-info">
                                        <h2>{supplierData.trade_name}</h2>
                                        <p className="cnpj"><span>CNPJ:</span> {supplierData.cnpj}</p>
                                        <p className="razao-social"><span>Razão social:</span> {supplierData.company_name}</p>
                                    </div>
                                </div>

                                <div className="supplier-description">
                                    <h3>Sobre o fornecedor</h3>
                                    <p dangerouslySetInnerHTML={{ __html: supplierData.description }} />
                                </div>

                                <StatusBadge status={supplierData.status} className="status-badge--detail" />

                                {!hasCalculationFeature && supplierData.status === "partially_connected" && (
                                    <div className="partial-connection-warning">
                                        <div>
                                            <h4>Conexao parcial</h4>
                                            <p>
                                                Este fornecedor está parcialmente conectado porque seu plano nao inclui o recurso de preco e estoque.
                                            </p>
                                        </div>
                                        <PlanFeatureUpgradeButton
                                            text="Ver upgrade"
                                            onClick={() => setShowUpgradeModal(true)}
                                        />
                                    </div>
                                )}

                                {supplierData.status !== "connected" && supplierData.status !== "partially_connected" && supplierData.is_associated === true && (
                                    <div className="box-bar-progress">
                                        <BarProgress showLabel={true} status={supplierData.status} isAssociated={supplierData.is_associated}/>
                                    </div>
                                )}

                                {supplierData.has_integration && hasCalculationFeature && (
                                <div className="api-keys-options">
                                    <label
                                        className={`api-keys-option ${hasApiKeys === true ? "is-selected" : ""}`}
                                        onClick={() => setHasApiKeys(true)}
                                    >
                                        <span className={`api-keys-option__radio ${hasApiKeys === true ? "api-keys-option__radio--selected" : ""}`}>
                                            <span className="api-keys-option__radio-dot" />
                                        </span>
                                        <span className="api-keys-option__content">
                                            <strong>Já tenho as chaves de API</strong>
                                            <span className="api-keys-option__desc">
                                                Os produtos serão cadastrados já com os preços do fornecedor.
                                            </span>
                                        </span>
                                    </label>
                                    {hasApiKeys && (
                                        <div className="api-keys-data">
                                            {supplierData.has_api_keys && !editingKeys ? (
                                                <div className="api-keys-data__saved">
                                                    <p className="api-keys-data__saved-text">
                                                        Você já informou as chaves da sua API. Caso queira alterá-las,{" "}
                                                        <button
                                                            type="button"
                                                            className="api-keys-data__link"
                                                            onClick={() => setEditingKeys(true)}
                                                        >
                                                            clique aqui
                                                        </button>
                                                        .
                                                    </p>
                                                    <div className="api-keys-data__warning">
                                                        <span className="api-keys-data__warning-icon">!</span>
                                                        <span>
                                                            <strong>Importante:</strong> a alteração é irreversível e as chaves não poderão ser recuperadas.
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {(supplierData.integration_fields || []).map((field) => (
                                                        <Input
                                                            key={field.name}
                                                            label={field.label}
                                                            type={field.type || "text"}
                                                            placeholder={field.placeholder}
                                                            value={integrationFieldsValues[field.name] ?? ""}
                                                            change={(value) => setIntegrationField(field.name, value)}
                                                        />
                                                    ))}
                                                    {(!supplierData.integration_fields || supplierData.integration_fields.length === 0) && (
                                                        <p className="api-keys-data__empty">Nenhum campo de integração configurado.</p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <label
                                        className={`api-keys-option ${hasApiKeys === false ? "is-selected" : ""}`}
                                        onClick={() => setHasApiKeys(false)}
                                    >
                                        <span className={`api-keys-option__radio ${hasApiKeys === false ? "api-keys-option__radio--selected" : ""}`}>
                                            <span className="api-keys-option__radio-dot" />
                                        </span>
                                        <span className="api-keys-option__content">
                                            <strong>Não tenho as chaves da API</strong>
                                            <span className="api-keys-option__desc">
                                                Solicite as chaves diretamente ao fornecedor. Até lá, os preços não ficarão disponíveis no sistema.
                                            </span>
                                        </span>
                                    </label>
                                </div>
                                )}

                                <div className="connect-section">
                                    {supplierData.is_associated === true && supplierData.has_integration && hasCalculationFeature && (
                                        <Button
                                            action={handleSaveCredentials}
                                            text="Salvar"
                                            btnClass="save-btn"
                                            svg="icon-save"
                                            loading={savingApiKeys}
                                        />
                                    )}
                                    <Button
                                        action={handleButtonClick}
                                        text={getButtonText()}
                                        btnClass={getButtonClass()}
                                        svg={getButtonClass() === "connect-btn" ? "icon-connect" : "icon-disconnect"}
                                    />
                                </div>

                                <div className="contact-info">
                                    {supplierData.phone &&
                                        <div className="contact-item">
                                            <Link to="#">
                                                <div className="box-icon">
                                                    <Icon name="phone" size={16} />
                                                </div>
                                                <div>
                                                    <p>Telefone</p>
                                                    <span>{supplierData.phone}</span>
                                                </div>
                                            </Link>
                                        </div>
                                    }
                                    {supplierData.email &&
                                        <div className="contact-item">
                                            <Link to="#">
                                                <div className="box-icon">
                                                    <Icon name="email" size={16} />
                                                </div>
                                                <div>
                                                    <p>E-mail</p>
                                                    <span>{supplierData.email}</span>
                                                </div>
                                            </Link>
                                        </div>
                                    }
                                </div>
                            </div>

                            <div className="featured-products">
                                <div className="products-header">
                                    <div>
                                        <h3>Produtos {supplierData.has_integration ? 'mais recentes' : 'em destaque'}</h3>
                                        <div className="products-count">{supplierData.productCount} produtos no total</div>
                                    </div>
                                    {!supplierData.has_integration && (
                                        <button className="view-all-btn" onClick={handleViewAllClick}>Ver todos</button>
                                    )}
                                </div>

                                <div className="products-list">
                                    {supplierData[supplierData.has_integration ? 'recent_products' : 'featured_products']?.map((product, index) => {
                                        let image = (
                                            <img
                                            src={require(`../../assets/images/svgs/default.svg`).default}
                                            alt={product.name}
                                            />
                                        );

                                        if (product.image?.length > 0 && product.image[0].image_url?.thumbnail) {
                                            image = (
                                            <img
                                                src={product.image[0].image_url.thumbnail}
                                                alt={product.name}
                                            />
                                            );
                                        }

                                        return (
                                            <div key={index} className="product-card">
                                            <div className="product-image">{image}</div>
                                            <div className="product-info">
                                                <h4>{product.name}</h4>
                                                <span className="product-id">{product.id}</span>
                                            </div>
                                            </div>
                                        );
                                    })}
                                </div>

                            </div>
                        </div>
                    )}
                </div>
            </PanelTemplate>
        </>
    );
};

export default IntegrationDetail;
